/**
 * Contracts for the in-app help (DESIGN UD-18, 2026-10-12): channel explainers and the glossary. Data lives in
 * `src/engine/channels/explain.ts` (one explainer per channel) and `src/web/strings-glossary.ts` (one entry per term).
 * Both are plain strings so the worker's report builders and the word test (T-UNIT-WORD-01) can read them. None may
 * contain a word matched by `BANNED_WORDS` (UX-WORD-01). Voice: plain, short, specific; no exclamation marks.
 */
import type { ChannelId } from '../engine/channels/types.ts';

/** What a channel measures and why it matters, in plain words. Shown wherever its ID or name appears. */
export interface ChannelExplainer {
	id: ChannelId;
	/** One sentence: what is counted or measured, in the channel's own units made plain. */
	measures: string;
	/** One sentence: the machine habit this captures, and so why the channel is in the ensemble. */
	why: string;
	/** Derived from `ChannelDef.direction`: which way is machine-like. Fixed wording, two values. */
	direction: 'higher' | 'lower';
	/** One line of innocent human causes of the same reading (the consider-the-opposite cue). */
	humanCauses: string;
	/** One line: when it abstains ("under 300 words"), from `minExposure` and `exposureUnit`. */
	needs: string;
}

/** The terms the interface defines in place. Every key has an entry in `GLOSSARY`; the test checks completeness. */
export const GLOSSARY_KEYS = [
	// scores and intervals
	'channel',
	'evidence-strength',
	'index',
	'kappa',
	'deviation',
	'interval-90',
	'interval-method',
	'text-sampling-variation',
	'contribution',
	'little-evidence',
	'hollow-whisker',
	// the headline
	'calibrated',
	'uncalibrated',
	'reference-point',
	'calibrated-confidence',
	'reference-prior',
	'strictest-stratum',
	'length-group',
	'evidence-coverage',
	'seed-rules-composite',
	'status',
	'model-card',
	'closest-categories',
	'human-range',
	// flags and places
	'hotspot',
	'elevated',
	'segment',
	'single-paragraph',
	'chapter-unit',
	'joint-segment',
	'p-reference',
	'p-background',
	'rate-ratio-reference',
	'rate-ratio-background',
	'engine-ranking',
	'evidence-span',
	'weight-zero',
	'feature',
	'withheld',
	'abstained',
	'length-dependent',
	'reads-other-way',
	'pattern-indicator',
	'text-hygiene',
	// families
	'family',
	'family-vocab',
	'family-formula',
	'family-rhetoric',
	'family-figure',
	'family-choreo',
	'family-rhythm',
	'family-prosody',
	'family-reuse',
	'family-texture',
	'family-envelope',
	'family-residue',
	'family-segment',
	'family-translation',
	// bench
	'stretch',
	'local-pass',
	'deep-rewrite',
	'economy',
	'standard-key',
	'kept-best',
	'dialogue-checks',
	'meaning-review',
	'signal-score',
	'ledger',
	'reasoning-tokens',
	'effort',
] as const;

export type GlossaryKey = (typeof GLOSSARY_KEYS)[number];

export interface GlossaryEntry {
	/** The term as it appears on screen (title case not required). */
	term: string;
	/** One sentence, plain words. */
	definition: string;
	/** Optional second sentence: how to use it when reading a result. */
	use?: string;
}

/** `Family` value → glossary key. */
export const FAMILY_KEY: Record<string, GlossaryKey> = {
	VOCAB: 'family-vocab',
	FORMULA: 'family-formula',
	RHETORIC: 'family-rhetoric',
	FIGURE: 'family-figure',
	CHOREO: 'family-choreo',
	RHYTHM: 'family-rhythm',
	PROSODY: 'family-prosody',
	REUSE: 'family-reuse',
	TEXTURE: 'family-texture',
	ENVELOPE: 'family-envelope',
	RESIDUE: 'family-residue',
	SEGMENT: 'family-segment',
	TRANSLATION: 'family-translation',
};
